import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import CoreStaffService from '../../services/CoreStaffService';
import { staffErrorText } from '../admin/staff/staffText';
import './Login.scss';

const REASON_TEXT = {
  used: 'Este link ya se usó. Pedí que te envíen uno nuevo.',
  expired: 'El link venció. Pedí que te envíen uno nuevo.',
  invalid: 'El link no es válido. Pedí que te envíen uno nuevo.',
};

/**
 * Pantalla pública: la persona llega desde el mail (invitación o restablecimiento) y define su contraseña.
 * El token viaja en el fragmento de la URL (`#/set-password?token=...`), así que no queda en los logs del servidor.
 */
const SetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [info, setInfo] = useState(null); // null = validando
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    if (!token) {
      setInfo({ valid: false, reason: 'invalid' });
      return undefined;
    }
    CoreStaffService.validateSetupToken(token)
      .then((data) => { if (active) setInfo(data); })
      .catch((err) => { if (active) setInfo({ valid: false, reason: 'error', message: staffErrorText(err, 'No se pudo validar el link. Probá de nuevo en unos minutos.') }); });
    return () => { active = false; };
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (password !== confirmation) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setSaving(true);
    try {
      await CoreStaffService.completeSetup(token, password);
      setDone(true);
    } catch (err) {
      setError(staffErrorText(err));
    } finally {
      setSaving(false);
    }
  };

  const isInvite = info?.type === 'invite';

  return (
    <div className="login-container">
      <div className="login-form">
        <h2>Administrador Axium</h2>
        {info === null && <p>Validando el link...</p>}

        {info && !info.valid && (
          <Alert severity="error">{info.message || REASON_TEXT[info.reason] || REASON_TEXT.invalid}</Alert>
        )}

        {info?.valid && !done && (
          <form onSubmit={handleSubmit}>
            <p style={{ marginBottom: 16 }}>
              {isInvite ? `Hola ${info.firstName}, definí tu contraseña para empezar.` : `Hola ${info.firstName}, elegí una contraseña nueva.`}
            </p>
            <div className="form-group">
              <label htmlFor="email">Correo</label>
              <input type="text" id="email" value={info.email} readOnly autoComplete="username" />
            </div>
            <div className="form-group">
              <label htmlFor="new-password">Contraseña nueva</label>
              <input type="password" id="new-password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <div className="form-group">
              <label htmlFor="confirm-password">Repetí la contraseña</label>
              <input type="password" id="confirm-password" autoComplete="new-password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} required />
            </div>
            {error && <Alert severity="error" style={{ marginBottom: '16px' }}>{error}</Alert>}
            <LoadingButton type="submit" variant="contained" loading={saving} fullWidth>Guardar contraseña</LoadingButton>
          </form>
        )}

        {done && (
          <>
            <Alert severity="success" style={{ marginBottom: '16px' }}>Listo, tu contraseña quedó definida.</Alert>
            <Button variant="contained" fullWidth onClick={() => navigate('/login')}>Ir a ingresar</Button>
          </>
        )}

        {info && !info.valid && (
          <Button style={{ marginTop: 16 }} fullWidth onClick={() => navigate('/login')}>Ir a ingresar</Button>
        )}
      </div>
    </div>
  );
};

export default SetPassword;
